import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { commonMessages, generalMessages } from 'src/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { CURRENCY_MAP } from 'src/features/moneyAccounts/utils/constants';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { useListCustomers } from 'src/features/customers/api/useListCustomers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { CURRENCY } from 'src/types';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import React from 'react';
import { FinancialDocumentFormSchemaValues } from 'src/features/financialDocuments/utils/formSchema';
import { FINANCIAL_DOCUMENT_STATUS, FINANCIAL_DOCUMENT_TYPE } from 'src/features/financialDocuments/utils/types';
import { FINANCIAL_DOCUMENT_TYPE_TRANSLATIONS } from 'src/features/financialDocuments/utils/constants';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { useListAccountingRanges } from 'src/features/accountingRanges/api/useListAccountingRanges';
import { accountingRangesMessages } from 'src/features/accountingRanges/utils/messages';
import { useListBillingEntities } from 'src/features/billing/api/useListBillingEntities';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils';
import { useListFinancialDocuments } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import { PopoverPortal } from '@radix-ui/react-popover';
import { Calendar } from 'src/registry/ui/Calendar';
import { useGetAccountingRange } from 'src/features/accountingRanges/api/useGetAccountingRange';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

type FinancialDocumentFormProps = {
    form: UseFormReturn<FinancialDocumentFormSchemaValues>;
    onSubmit: (values: FinancialDocumentFormSchemaValues) => void;
    isEditMode?: boolean;
    isDraft?: boolean;
};

function FinancialDocumentForm({ onSubmit, form, isEditMode = false, isDraft }: FinancialDocumentFormProps) {
    const { formatMessage, formatDate } = useIntl();
    const { customerId, rangeId } = useParams();

    const disableCustomerSelect = Boolean(customerId);
    const disableAccountingRangeSelect = Boolean(rangeId);

    const { type, supplier, accounting_range, currency } = form.watch();

    const [searchCustomer, setSearchCustomer] = React.useState<string>('');
    const [searchFinancialDoc, setSearchFinancialDoc] = React.useState<string>('');

    const { data: customersData } = useListCustomers({
        search: searchCustomer,
    });
    const { data: accountingRangesData } = useListAccountingRanges({
        ...(supplier && { billing_entity: supplier }),
    });

    const { data: accountingRange } = useGetAccountingRange({
        id: accounting_range ?? '',
        config: { enabled: !!accounting_range },
    });

    const isAccountingRangeManual = !!accountingRange?.is_manual;

    const { data: billingEntities } = useListBillingEntities({});
    const { data: financialDocuments } = useListFinancialDocuments({
        status: FINANCIAL_DOCUMENT_STATUS.ISSUED,
        currency: currency,
        search: searchFinancialDoc,
    });

    const [openTaxEventCalendar, setOpenTaxEventCalendar] = React.useState<boolean>(false);
    const [openPaymentDueCalendar, setOpenPaymentDueCalendar] = React.useState<boolean>(false);
    const [openIssueDateCalendar, setOpenIssueDateCalendar] = React.useState<boolean>(false);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <SelectFormField<FinancialDocumentFormSchemaValues>
                form={form}
                name="type"
                required
                label={formatMessage(paymentsMessages.type)}
                options={Object.values(FINANCIAL_DOCUMENT_TYPE)}
                optionTranslationMap={FINANCIAL_DOCUMENT_TYPE_TRANSLATIONS}
                isDisabled={isEditMode}
            />
            <SelectFormField<FinancialDocumentFormSchemaValues>
                form={form}
                name="currency"
                required
                label={formatMessage(commonMessages.currency)}
                options={Object.values(CURRENCY)}
                optionTranslationMap={CURRENCY_MAP}
                isDisabled={isEditMode && !isDraft}
            />

            <FormField
                control={form.control}
                name="supplier"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...financialDocumentsMessages.financialDocBillingEntity} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={isEditMode && !isDraft}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(financialDocumentsMessages.financialDocBillingEntity)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {billingEntities?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SearchSelectFormField
                name="customer"
                control={form.control}
                label={formatMessage(financialDocumentsMessages.financialDocCustomer)}
                items={customersData?.results ?? []}
                clearable
                placeholder={`${formatMessage(financialDocumentsMessages.financialDocCustomer)}...`}
                searchPlaceholder={formatMessage(financialDocumentsMessages.searchFinancialDocCustomer)}
                emptyTitle={formatMessage(financialDocumentsMessages.noFinancialDocCustomer)}
                emptyDescription={formatMessage(financialDocumentsMessages.noFinancialDocCustomerFound)}
                getItemValue={(customer) => customer.id}
                getItemLabel={(customer) => customer.name}
                getItemKeywords={(customer) => [customer.name]}
                disabled={(isEditMode && !isDraft) || disableCustomerSelect}
                loadOptions={setSearchCustomer}
            />
            <FormField
                control={form.control}
                name="accounting_range"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...accountingRangesMessages.accountingRanges} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={(isEditMode && !isDraft) || disableAccountingRangeSelect}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(accountingRangesMessages.accountingRange)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {accountingRangesData?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            {isAccountingRangeManual && (
                <InputFormField<FinancialDocumentFormSchemaValues>
                    form={form}
                    name="number"
                    label={formatMessage(financialDocumentsMessages.number)}
                    required={false}
                    disabled={isEditMode && !isDraft}
                />
            )}

            <InputFormField<FinancialDocumentFormSchemaValues>
                form={form}
                name="public_description"
                type="textarea"
                required={false}
                label={formatMessage(financialDocumentsMessages.publicDescription)}
            />
            <InputFormField<FinancialDocumentFormSchemaValues>
                form={form}
                name="internal_description"
                type="textarea"
                required={false}
                label={formatMessage(financialDocumentsMessages.internalDescription)}
            />
            <FormField
                control={form.control}
                name="payment_due_date"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...financialDocumentsMessages.paymentDueDate} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openPaymentDueCalendar} onOpenChange={setOpenPaymentDueCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenPaymentDueCalendar(true)}
                                                disabled={isEditMode && !isDraft}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenPaymentDueCalendar(false);
                                                        }
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="tax_event_date"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...financialDocumentsMessages.taxEventDate} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openTaxEventCalendar} onOpenChange={setOpenTaxEventCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenTaxEventCalendar(true)}
                                                disabled={isEditMode && !isDraft}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenTaxEventCalendar(false);
                                                        }
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="issue_due_date"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...financialDocumentsMessages.issueDate} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openIssueDateCalendar} onOpenChange={setOpenIssueDateCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenIssueDateCalendar(true)}
                                                disabled={isEditMode && !isDraft}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenTaxEventCalendar(false);
                                                        }
                                                    }}
                                                    disabled={{ after: new Date() }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            {(type === FINANCIAL_DOCUMENT_TYPE.DEBIT_NOTE || type === FINANCIAL_DOCUMENT_TYPE.CREDIT_NOTE) && (
                <SearchSelectFormField
                    name="financial_document"
                    control={form.control}
                    label={formatMessage(financialDocumentsMessages.financialDocument)}
                    items={financialDocuments?.results ?? []}
                    clearable
                    placeholder={`${formatMessage(financialDocumentsMessages.financialDocument)}...`}
                    searchPlaceholder={formatMessage(financialDocumentsMessages.searchFinancialDocument)}
                    emptyTitle={formatMessage(financialDocumentsMessages.noFinancialDocument)}
                    emptyDescription={formatMessage(financialDocumentsMessages.noFinancialDocumentFound)}
                    getItemValue={(d) => d.id}
                    getItemLabel={(doc) => {
                        const typeLabel = formatMessage(FINANCIAL_DOCUMENT_TYPE_TRANSLATIONS[doc.type]);
                        if (doc.number && doc.type) return `${doc.number}-${typeLabel}`;
                        if (doc.number) return doc.number;
                        return typeLabel;
                    }}
                    getItemKeywords={(doc) => [doc.number, doc.type]}
                    loadOptions={setSearchFinancialDoc}
                    disabled={isEditMode}
                />
            )}
        </Form>
    );
}

export default FinancialDocumentForm;
